import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserCircle, Signature, FloppyDisk } from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";

export default function ProfilePage() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fullName, setFullName] = useState("");
  const [designation, setDesignation] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/users/me");
      setMe(data);
      setFullName(data.full_name || "");
      setDesignation(data.designation || "");
    } catch (e) { toast.error("Failed to load profile"); }
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    if (!fullName.trim()) { toast.error("Name cannot be empty"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me", { full_name: fullName, designation });
      setMe(data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const saveSignature = async (dataUrl) => {
    setBusy(true);
    try {
      if (dataUrl) {
        await api.put("/users/me/signature", { data_url: dataUrl });
        toast.success("Signature saved");
      } else {
        await api.delete("/users/me/signature");
        toast.info("Signature removed");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!me) return <div className="p-8 text-sm text-slate-500">Loading profile…</div>;

  const dirty = (fullName !== (me.full_name || "")) || (designation !== (me.designation || ""));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl" data-testid="profile-page">
      <div className="label-micro">Your account</div>
      <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Profile</h1>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5" data-testid="profile-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-sm bg-slate-100 flex items-center justify-center shrink-0"><UserCircle size={26} className="text-slate-700"/></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-600">{me.email}</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(me.roles || []).map(r => (
                <span key={r} className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-slate-100 text-slate-700">{r}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <label className="block">
            <span className="label-micro">Full name *</span>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)}
                   data-testid="profile-full-name"
                   className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </label>
          <label className="block">
            <span className="label-micro">Designation</span>
            <input value={designation} onChange={(e) => setDesignation(e.target.value)}
                   placeholder="e.g., Head of Sales — India"
                   data-testid="profile-designation"
                   className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-slate-500 mt-1">Shown under your signature on signed documents and proposal exports. Editing it here immediately updates future signatures.</p>
          </label>
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={saveProfile} disabled={!dirty || busy} data-testid="profile-save"
                  className="text-sm inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-sm px-4 py-2 transition-colors">
            <FloppyDisk size={14}/>{busy ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>

      <div className="mt-6" data-testid="signature-section">
        <div className="flex items-center gap-2 mb-2">
          <Signature size={18} className="text-blue-600"/>
          <h2 className="font-display font-bold text-lg">Your signature</h2>
        </div>
        <p className="text-sm text-slate-600 mb-3">Draw with your mouse / finger or upload a transparent PNG. This signature is auto-stamped when you Submit or Approve a proposal, and used when you digitally sign external documents. Your name and designation appear below the signature on every stamp.</p>
        <SignaturePad value={me.signature_data_url || null} onChange={saveSignature} disabled={busy} testid="profile-sig"/>
        {me.signature_updated_at && <div className="text-[11px] text-slate-500 mt-2">Last updated {new Date(me.signature_updated_at).toLocaleString()}</div>}
      </div>
    </div>
  );
}
